import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/libs/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        primary:
          'border-0 bg-primary-action-bg text-primary-action-text shadow-sm hover:bg-primary-action-bg-hover active:bg-primary-action-bg-active focus-visible:ring-ring/50 disabled:pointer-events-auto disabled:cursor-not-allowed disabled:bg-primary-action-disabled-bg disabled:text-primary-action-disabled-text disabled:opacity-100',
        'danger-fill':
          'border-0 bg-danger-fill-bg text-danger-fill-text shadow-sm hover:bg-danger-fill-hover active:bg-danger-fill-active focus-visible:ring-destructive/20 disabled:pointer-events-auto disabled:cursor-not-allowed disabled:bg-danger-fill-disabled-bg disabled:text-danger-fill-disabled-text disabled:opacity-100',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/75 dark:hover:bg-destructive/90',
        'danger-outline':
          'border border-danger-outline-border bg-transparent text-danger-outline-text shadow-xs hover:border-danger-outline-hover-border hover:bg-danger-outline-hover-bg active:bg-danger-outline-active-bg focus-visible:border-danger-outline-border focus-visible:ring-destructive/20 disabled:pointer-events-auto disabled:cursor-not-allowed disabled:border-danger-outline-disabled-border disabled:text-danger-outline-disabled-text disabled:opacity-100',
        'secondary-outline':
          'border border-secondary-outline-border bg-transparent text-secondary-outline-text shadow-xs hover:border-secondary-outline-hover-border hover:bg-secondary-outline-hover-bg active:bg-secondary-outline-active-bg focus-visible:border-secondary-outline-border focus-visible:ring-ring/20 disabled:pointer-events-auto disabled:cursor-not-allowed disabled:border-secondary-outline-disabled-border disabled:text-secondary-outline-disabled-text disabled:opacity-100',
        outline:
          'border border-border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/35 dark:border-border dark:hover:bg-input/70 dark:hover:border-ring/60',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/85 dark:hover:bg-secondary/75',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/70',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
);

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={ref}
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);

Button.displayName = 'Button';

export { Button, buttonVariants };
